import React, { useEffect, useRef, useState } from 'react';
import { X, Send, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApiQuery, useApiMutation } from '../../hooks/useApiQuery';

const FACES = [
  { value: 1, emoji: '😣', label: 'لم أحقق الاستفادة المطلوبة' },
  { value: 2, emoji: '🙁', label: 'استفادتي كانت محدودة' },
  { value: 3, emoji: '🙂', label: 'استفدت إلى حدٍ ما' },
  { value: 4, emoji: '😊', label: 'استفدت بشكل جيد' },
  { value: 5, emoji: '😍', label: 'استفدت كثيرًا' },
];

/**
 * «تقييم الاستفادة من الحصة» — popup modal (Sep 2026 UX rework).
 * Appears after the student leaves a live session (or on first
 * dashboard visit if missed). Selecting a rating instantly reveals
 * the note field with a motivating placeholder. X = dismissed for
 * this session; next prompt arrives after the next attended lesson.
 */
export const LessonFeedbackModal = () => {
  const { user } = useAuth();
  const isStudent = user?.role === 'Student';
  const [rating, setRating] = useState(null);
  const [note, setNote] = useState('');
  const [done, setDone] = useState(false);
  const [closed, setClosed] = useState(false);
  const [visible, setVisible] = useState(false);
  const noteRef = useRef(null);

  const { data } = useApiQuery(
    ['lesson-feedback', 'pending'], '/lesson-feedback/pending',
    { silent: true, enabled: isStudent },
  );
  const prompt = data?.prompt;

  useEffect(() => {
    if (!prompt || closed) return undefined;
    // Defer while the first-visit tour overlay is on screen.
    const tourActive = () => !!document.querySelector('[data-testid="student-tour-root"]');
    let iv = null;
    const t = setTimeout(() => {
      if (!tourActive()) { setVisible(true); return; }
      iv = setInterval(() => {
        if (!tourActive()) { setVisible(true); clearInterval(iv); }
      }, 1500);
    }, 700);
    return () => { clearTimeout(t); if (iv) clearInterval(iv); };
  }, [prompt, closed]);

  useEffect(() => {
    if (rating && noteRef.current) noteRef.current.focus();
  }, [rating]);

  const submitMut = useApiMutation('post', () => `/lesson-feedback/${prompt.id}/submit`, {
    silent: true,
    invalidates: [['lesson-feedback', 'pending']],
    onSuccess: () => {
      setDone(true);
      setTimeout(() => setClosed(true), 2800);
    },
  });
  const dismissMut = useApiMutation('post', () => `/lesson-feedback/${prompt.id}/dismiss`, {
    silent: true,
    invalidates: [['lesson-feedback', 'pending']],
  });

  if (!isStudent || !prompt || closed || !visible) return null;

  const handleClose = () => {
    if (!done) dismissMut.mutate({});
    setClosed(true);
  };

  return (
    <div
      data-testid="lesson-feedback-modal-overlay"
      dir="rtl"
      className="fixed inset-0 z-[110] flex items-center justify-center px-4"
      style={{ background: 'rgba(4, 8, 22, 0.72)', backdropFilter: 'blur(6px)' }}
    >
      <div
        data-testid="lesson-feedback-modal"
        className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl mesp-anim-fade-in relative"
        style={{
          background: 'linear-gradient(180deg, #0F1F3E 0%, #0A1128 100%)',
          boxShadow: '0 24px 60px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,162,78,0.3)',
        }}
      >
        <div
          className="h-1 w-full"
          style={{ background: 'linear-gradient(90deg, transparent, #C9A24E, #E5C158, #C9A24E, transparent)' }}
        />

        {done ? (
          <div className="p-8 text-center" data-testid="lesson-feedback-thanks">
            <div
              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center mesp-anim-fade-in"
              style={{ background: 'linear-gradient(160deg, #E5C158 0%, #C99A2F 100%)' }}
            >
              <Sparkles size={28} className="text-[#0A1128]" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">شكراً لك! 🌟</h3>
            <p className="text-sm text-amber-200/85 leading-relaxed">
              تقييمك يساهم في تطوير تجربة التعلّم وتحسين حصصك القادمة
            </p>
          </div>
        ) : (
          <div className="p-6 sm:p-7">
            <button
              type="button"
              data-testid="lesson-feedback-close-btn"
              onClick={handleClose}
              aria-label="إغلاق"
              className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-150"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' }}
            >
              <X size={16} />
            </button>

            <h2 className="text-lg sm:text-xl font-bold text-white text-center mb-1.5">
              كيف كانت استفادتك من الحصة؟
            </h2>
            <p className="text-[12px] text-[#8B9AC0] text-center mb-5">
              {prompt.classroom_name ? `قاعة ${prompt.classroom_name}` : 'حصتك الأخيرة'}
              {prompt.session_date ? ` · ${prompt.session_date}` : ''}
            </p>

            <div className="flex justify-between gap-1.5 mb-4" data-testid="lesson-feedback-faces">
              {FACES.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  data-testid={`lesson-feedback-face-${f.value}`}
                  onClick={() => setRating(f.value)}
                  className="flex-1 flex flex-col items-center gap-1.5 rounded-xl py-2.5 transition-transform duration-150 hover:scale-110"
                  style={{
                    background: rating === f.value ? 'rgba(201,162,78,0.22)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${rating === f.value ? 'rgba(201,162,78,0.65)' : 'rgba(255,255,255,0.08)'}`,
                    transform: rating === f.value ? 'scale(1.1)' : undefined,
                  }}
                >
                  <span className="text-2xl leading-none">{f.emoji}</span>
                  <span
                    className="text-[9px]"
                    style={{ color: rating === f.value ? '#E5C158' : '#8B9AC0' }}
                  >
                    {f.label}
                  </span>
                </button>
              ))}
            </div>

            {rating && (
              <div className="mesp-anim-fade-in">
                <textarea
                  ref={noteRef}
                  data-testid="lesson-feedback-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 500))}
                  placeholder="هل لديك ملاحظة أو اقتراح حول الحصة؟ يمكنك مشاركته هنا."
                  rows={3}
                  className="w-full rounded-xl text-white text-[13px] p-3.5 mb-2 outline-none resize-none leading-relaxed"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(201,162,78,0.3)',
                  }}
                />
                <p className="text-[11px] text-[#8B9AC0] text-center mb-4" data-testid="lesson-feedback-helper">
                  رأيك يساعدنا على تطوير تجربة التعلّم وتحسين الحصص.
                </p>
                <button
                  type="button"
                  data-testid="lesson-feedback-submit-btn"
                  disabled={submitMut.isPending}
                  onClick={() => submitMut.mutate({ rating, note: note.trim() || null })}
                  className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all duration-150 hover:brightness-110 active:translate-y-px disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(180deg, #E5C158 0%, #C99A2F 100%)',
                    color: '#0A1128',
                    boxShadow: '0 8px 22px -8px rgba(201,162,78,0.55)',
                  }}
                >
                  {submitMut.isPending
                    ? <Loader2 size={15} className="animate-spin" />
                    : <Send size={15} />}
                  إرسال التقييم
                </button>
              </div>
            )}

            {!rating && (
              <p className="text-[11px] text-[#5C6B8C] text-center">
                اختر مدى استفادتك من الحصة
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonFeedbackModal;
